import { useEffect, useRef } from "react";

import {
    useFastshiftAnalyticsData,
    useFireAnalyticsEvents
} from "@/components/fastshift/analytics";
import {
    SourceKey,
    ActionKey,
    ActionSubjectKey,
    ActionSubjectIdKey
} from "@/components/fastshift/analytics/constants";
import { useFastshiftSelector } from "@/components/fastshift/src/state";

import { buildForYouPageDetailsAttributes } from "./utils";

import { useSummary } from "./";

export const useDashboardPageAnalytics = () => {
    const { data: customerDomain } = useFastshiftSelector(
        (state) => state.customerDomain
    );
    const { data: userDetails } = useFastshiftSelector(
        (state) => state.userDetails
    );
    const { data: supportRequests, pending: supportRequestsPending } =
        useFastshiftSelector((state) => state.supportRequests);

    const domainOrg = useFastshiftSelector((state) => state.selectedDomainOrg);

    const {
        summaryData,
        isL1AssessmentsLoading,
        isMigrationPlanTotalsLoading,
        isInstancesLoading,
        isEmpty: summaryEmpty
    } = useSummary();

    const summaryLoading =
        isL1AssessmentsLoading ||
        isMigrationPlanTotalsLoading ||
        isInstancesLoading;

    const { identityAttrs } = useFastshiftAnalyticsData();
    const { fireScreenEvent, fireTrackEvent } =
        useFireAnalyticsEvents(identityAttrs);

    const lastFiredKeyRef = useRef<string | null>(null);

    // Screen event
    useEffect(() => {
        if (!identityAttrs) return;
        fireScreenEvent({
            name: SourceKey.FASTSHIFT_PORTAL_FOR_YOU_SCREEN
        });
    }, [fireScreenEvent, identityAttrs]);

    // Track event - when a different org is selected and all migration data is loaded
    useEffect(() => {
        if (
            !customerDomain ||
            !identityAttrs ||
            !domainOrg ||
            !supportRequests ||
            summaryLoading ||
            supportRequestsPending
        )
            return;

        const attrs = buildForYouPageDetailsAttributes({
            customerDomain,
            domainOrg,
            summaryData,
            summaryEmpty,
            supportRequests
        });
        if (!attrs) return;

        const key = JSON.stringify({
            ...identityAttrs,
            ...attrs
        });

        if (lastFiredKeyRef.current === key) {
            return;
        }

        fireTrackEvent({
            action: ActionKey.DISPLAYED,
            actionSubject: ActionSubjectKey.SECTION,
            actionSubjectId: ActionSubjectIdKey.FOR_YOU_PAGE_DETAILS,
            source: SourceKey.FASTSHIFT_PORTAL_FOR_YOU_SCREEN,
            attributes: attrs
        });
        lastFiredKeyRef.current = key;
    }, [
        customerDomain,
        userDetails,
        domainOrg,
        summaryLoading,
        summaryEmpty,
        summaryData,
        supportRequests,
        supportRequestsPending,
        identityAttrs,
        fireTrackEvent
    ]);
};
